import { useState } from "react";
import { Search, ExternalLink } from "lucide-react";
import { useLocation } from "wouter";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export default function HeroBanner() {
  const [searchQuery, setSearchQuery] = useState("");
  const [, setLocation] = useLocation();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // In a real implementation, this would go to a search results page
      setLocation(`/destinations?search=${encodeURIComponent(searchQuery)}`);
    }
  };

  return (
    <section className="relative bg-[#E30A17] h-[500px] md:h-[600px]">
      <div className="absolute inset-0 bg-black bg-opacity-30 z-10"></div>
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1524231757912-21f4fe3a7200?ixlib=rb-1.2.1&auto=format&fit=crop&w=1920&q=80"
          alt="Istanbul panorama with Blue Mosque"
          className="w-full h-full object-cover"
          width={1920}
          height={600}
        />
      </div>
      <div className="container mx-auto px-4 h-full flex flex-col justify-center items-center relative z-20">
        <h1 className="text-4xl md:text-6xl font-sans font-bold text-white text-center mb-4 drop-shadow-lg">
          Turkey Travel Guide 2026
        </h1>
        <p className="text-xl md:text-2xl text-white text-center mb-4 max-w-3xl drop-shadow-md">
          Discover the rich culture, stunning landscapes, and warm hospitality of Türkiye
        </p>
        <p className="text-lg text-white text-center mb-8 max-w-2xl drop-shadow-md">
          From the historic streets of Istanbul to the pristine beaches of Bodrum, find everything you need for an unforgettable Turkish adventure
        </p>
        
        {/* Search Box */}
        <div className="w-full max-w-2xl bg-white rounded-lg shadow-lg p-4">
          <form onSubmit={handleSearch} className="flex flex-col md:flex-row">
            <Input
              type="text"
              placeholder="Search destinations, attractions, or topics..."
              aria-label="Search destinations, attractions, or topics"
              className="flex-1 p-3 border border-[#E0E0E0] rounded-lg md:rounded-r-none md:border-r-0 mb-2 md:mb-0 focus:outline-none focus:ring-2 focus:ring-[#E30A17]"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <Button 
              type="submit"
              className="bg-[#E30A17] text-white font-sans font-semibold py-3 px-6 rounded-lg md:rounded-l-none hover:bg-opacity-90 transition duration-200"
            >
              <Search className="mr-2 h-4 w-4" /> Search
            </Button>
          </form>
        </div>

        {/* External Links */}
        <div className="mt-6 flex flex-wrap justify-center gap-4">
          <a 
            href="https://www.goturkiye.com/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-white hover:text-gray-200 flex items-center text-sm"
          >
            Official Tourism Portal <ExternalLink className="ml-1 h-4 w-4" />
          </a>
          <a 
            href="https://www.kultur.gov.tr/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-white hover:text-gray-200 flex items-center text-sm"
          >
            Ministry of Culture <ExternalLink className="ml-1 h-4 w-4" />
          </a>
          <a 
            href="https://www.ktb.gov.tr/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-white hover:text-gray-200 flex items-center text-sm"
          >
            Tourism Information <ExternalLink className="ml-1 h-4 w-4" />
          </a>
        </div>
      </div>
    </section>
  );
}
